import { Box, Slab } from '../../render/parts'
import type { AssetContext, AssetDefinition } from '../types'

/**
 * Floor-standing glass screen: a full-height aluminium frame with a mid rail,
 * glazed with two panes.
 *
 * The `glass` tone is almost invisible on its own (22 % opacity, no depth
 * write), so the frame is what actually draws the object: full-width top and
 * bottom rails, two stiles tucked just inside them, and a mid rail that gives
 * the pane a horizontal line to read against.
 */

const HEIGHT = 2.2
const DEPTH = 0.06
/** Floor pads lift the frame, leaving a shadow gap under the bottom rail. */
const PAD_HEIGHT = 0.02
const FRAME_BASE = 0.012
const BOTTOM_RAIL = 0.07
const TOP_RAIL = 0.045
/** Y of the mid rail's centre. */
const MID_Y = 1.08
const MID_RAIL = 0.032

function PartitionGlass({ variant = 'w120' }: AssetContext) {
  const width = variant === 'w090' ? 0.9 : 1.2

  const frameTop = HEIGHT - TOP_RAIL
  const railTop = FRAME_BASE + BOTTOM_RAIL
  // Stiles run between the rails, overlapping each by 10 mm.
  const stileBottom = railTop - 0.01
  const stileTop = frameTop + 0.01
  // Panes bury their edges in the rails, stiles and mid rail.
  const lowerBottom = railTop - 0.01
  const lowerTop = MID_Y + 0.012
  const upperBottom = MID_Y - 0.012
  const upperTop = frameTop + 0.01

  return (
    <group>
      {/* floor pads */}
      {[-1, 1].map((side) => (
        <Slab
          key={side}
          tone="metalDark"
          size={[0.16, 0.05, PAD_HEIGHT]}
          radius={0.006}
          position={[side * (width / 2 - 0.14), PAD_HEIGHT / 2, 0]}
        />
      ))}

      {/* bottom rail — the only member at full footprint depth */}
      <Box
        tone="metal"
        size={[width, BOTTOM_RAIL, DEPTH]}
        position={[0, FRAME_BASE + BOTTOM_RAIL / 2, 0]}
      />
      {/* head rail */}
      <Box tone="metal" size={[width, TOP_RAIL, 0.05]} position={[0, HEIGHT - TOP_RAIL / 2, 0]} />

      {/* stiles, 3 mm inside the rails so no two faces share a plane */}
      {[-1, 1].map((side) => (
        <Box
          key={side}
          tone="metal"
          size={[0.045, stileTop - stileBottom, 0.044]}
          position={[side * (width / 2 - 0.0255), (stileBottom + stileTop) / 2, 0]}
        />
      ))}

      {/* mid rail, ends buried in the stiles */}
      <Box tone="metal" size={[width - 0.06, MID_RAIL, 0.038]} position={[0, MID_Y, 0]} />

      {/* glazing */}
      <Box
        tone="glass"
        size={[width - 0.08, lowerTop - lowerBottom, 0.01]}
        position={[0, (lowerBottom + lowerTop) / 2, 0]}
      />
      <Box
        tone="glass"
        size={[width - 0.08, upperTop - upperBottom, 0.01]}
        position={[0, (upperBottom + upperTop) / 2, 0]}
      />
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'partition-glass',
  name: 'Mampara de vidrio',
  category: 'partitions',
  footprint: [1.2, DEPTH],
  height: HEIGHT,
  variants: [
    { id: 'w120', name: '1.2 m', footprint: [1.2, DEPTH] },
    { id: 'w090', name: '0.9 m', footprint: [0.9, DEPTH] },
  ],
  keywords: [
    'mampara',
    'vidrio',
    'cristal',
    'divisor',
    'separador',
    'glass',
    'partition',
    'screen',
    'divider',
  ],
  Component: PartitionGlass,
}
